/**
 * Maps GET /api/afas/openstaand/verzoeken groups to sidebar table rows.
 *
 * Short verzoeken: one row per school + Verzoek (substitutionId) + vervanger —
 * same substitutor on two verzoeken must both appear (matches Bluebox tree).
 * Langdurige: one row per prolonged_application (prolongedId).
 *
 * @returns {array<{id: string, school: string, verzoekId: number, naam: string, dagtype: string, status: string, substitutionId: number, substitutorAdminId: number, prolongedId?: number|null}>}
 */
export const mapOpenstaandSidebarRows = (apiGroups = []) => {
    const rows = [];
    const seenProlongedIds = new Set();
    const seenShortKeys = new Set();

    apiGroups.forEach((group) => {
        (group.items ?? []).forEach((school) => {
            (school.verzoeken ?? []).forEach((verzoek) => {
                const substitutionId = Number(verzoek.id);

                (verzoek.personen ?? []).forEach((person) => {
                    const adminId = Number(person.id);
                    if (!adminId) {
                        return;
                    }

                    const prolongedId = person.prolongedId
                        ? Number(person.prolongedId)
                        : null;

                    if (prolongedId) {
                        if (seenProlongedIds.has(prolongedId)) {
                            return;
                        }
                        seenProlongedIds.add(prolongedId);
                        rows.push({
                            id: `prolonged-${prolongedId}`,
                            school: school.label ?? '',
                            verzoekId: substitutionId,
                            naam: person.label,
                            dagtype: person.dagtype ?? person.dagType ?? '—',
                            status: person.status ?? 'open',
                            substitutionId,
                            substitutorAdminId: adminId,
                            prolongedId,
                        });
                        return;
                    }

                    const shortKey = `${school.id}-${substitutionId}-${adminId}`;
                    if (seenShortKeys.has(shortKey)) {
                        return;
                    }
                    seenShortKeys.add(shortKey);

                    rows.push({
                        id: `verzoek-${substitutionId}-admin-${adminId}`,
                        school: school.label ?? '',
                        verzoekId: substitutionId,
                        naam: person.label,
                        dagtype: person.dagtype ?? person.dagType ?? '—',
                        status: person.status ?? 'open',
                        substitutionId,
                        substitutorAdminId: adminId,
                        prolongedId: null,
                    });
                });
            });
        });
    });

    rows.sort((a, b) => {
        const schoolCmp = a.school.localeCompare(b.school, 'nl');
        if (schoolCmp !== 0) {
            return schoolCmp;
        }
        if (a.verzoekId !== b.verzoekId) {
            return a.verzoekId - b.verzoekId;
        }
        return a.naam.localeCompare(b.naam, 'nl');
    });

    return rows;
};

/**
 * @deprecated Prefer mapOpenstaandSidebarRows for the table sidebar layout.
 */
export const mapOpenstaandSidebarGroups = (apiGroups = []) =>
    apiGroups.map((group, groupIndex) => {
        const schools = group.items ?? [];
        const groupLabel =
            schools[0]?.label ?? group.label ?? `Organisatie ${groupIndex + 1}`;

        const items = [];

        schools.forEach((school) => {
            const schoolPersons = [];
            const seenKeys = new Set();

            (school.verzoeken ?? []).forEach((verzoek) => {
                const substitutionId = Number(verzoek.id);

                (verzoek.personen ?? []).forEach((person) => {
                    const adminId = Number(person.id);
                    if (!adminId) {
                        return;
                    }

                    const key = `${substitutionId}-${adminId}-${person.prolongedId ?? ''}`;
                    if (seenKeys.has(key)) {
                        return;
                    }
                    seenKeys.add(key);

                    schoolPersons.push({
                        id: `verzoek-${substitutionId}-admin-${adminId}`,
                        label: person.label,
                        type: 'person',
                        status: person.status ?? 'open',
                        substitutionId,
                        substitutorAdminId: adminId,
                        prolongedId: person.prolongedId
                            ? Number(person.prolongedId)
                            : null,
                    });
                });
            });

            if (schoolPersons.length === 0) {
                return;
            }

            schoolPersons.sort((a, b) => {
                if (a.substitutionId !== b.substitutionId) {
                    return a.substitutionId - b.substitutionId;
                }
                return a.label.localeCompare(b.label, 'nl');
            });

            items.push({
                id: `school-${school.id}`,
                label: school.label,
                type: 'school',
            });
            items.push(...schoolPersons);
        });

        return {
            id: group.id ?? `group-${groupIndex}`,
            label: groupLabel,
            items,
        };
    });
